// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    firstButtonText = "firstButtonText",
    firstButtonLink = "firstButtonLink",
    firstButtonOpenInNewWindow = "firstButtonOpenInNewWindow",
    secondButtonText = "secondButtonText",
    secondButtonLink = "secondButtonLink",
    secondButtonOpenInNewWindow = "secondButtonOpenInNewWindow",
    thirdButtonText = "thirdButtonText",
    thirdButtonLink = "thirdButtonLink",
    thirdButtonOpenInNewWindow = "thirdButtonOpenInNewWindow",
}

export interface SignedInPageActionsProps extends WidgetProps {
    fields: {
        [fields.firstButtonText]: string;
        [fields.firstButtonLink]: LinkFieldValue;
        [fields.firstButtonOpenInNewWindow]: boolean;
        [fields.secondButtonText]: string;
        [fields.secondButtonLink]: LinkFieldValue;
        [fields.secondButtonOpenInNewWindow]: boolean;
        [fields.thirdButtonText]: string;
        [fields.thirdButtonLink]: LinkFieldValue;
        [fields.thirdButtonOpenInNewWindow]: boolean;
    };
}

export interface SignedInPageActionsStyles {
    buttonStyle?: ButtonPresentationProps;
    styledWrapperStyle?: InjectableCss;
}

export const SignedInPageActionsStyles: SignedInPageActionsStyles = {
    styledWrapperStyle: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            background: rgba(255, 255, 255, 0.7);
            border-radius: ${pxToRem(20)};
            height: auto;
            padding: ${pxToRem(32, 20)};
            width: 100%;
            gap: ${pxToRem(20)};
        `,
    },
    buttonStyle: {
        sizeVariant: "large",
    },
};

const styles = SignedInPageActionsStyles;

const SignedInPageActions: FC<SignedInPageActionsProps> = ({ fields }) => {
    const history = useHistory();
    const { isInShell } = useShellContext();
    const firstLink = useGetLink(fields.firstButtonLink);
    const secondLink = useGetLink(fields.secondButtonLink);
    const thirdLink = useGetLink(fields.thirdButtonLink);

    const onClick = (
        link: ReturnType<typeof useGetLink> | { url: string | undefined; title: string | undefined },
        openInNewWindow: boolean,
    ) => {
        if (!link.url) {
            return;
        }

        if (link.url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            if (openInNewWindow) {
                window.open(link.url, "_blank");
            } else {
                window.location.href = link.url;
            }
        } else if (openInNewWindow) {
            window.open(link.url, "_blank");
        } else {
            history.push(link.url);
        }
    };

    const firstOnClick = () => {
        onClick(firstLink, fields.firstButtonOpenInNewWindow);
    };

    const secondOnClick = () => {
        onClick(secondLink, fields.secondButtonOpenInNewWindow);
    };

    const thirdOnClick = () => {
        onClick(thirdLink, fields.thirdButtonOpenInNewWindow);
    };

    return (
        <StyledWrapper {...styles.styledWrapperStyle}>
            {firstLink.url && fields.firstButtonText && (
                <Button {...styles.buttonStyle} onClick={firstOnClick}>
                    {fields.firstButtonText}
                </Button>
            )}
            {secondLink.url && fields.secondButtonText && (
                <Button {...styles.buttonStyle} onClick={secondOnClick}>
                    {fields.secondButtonText}
                </Button>
            )}
            {thirdLink.url && fields.thirdButtonText && (
                <Button {...styles.buttonStyle} onClick={thirdOnClick}>
                    {fields.thirdButtonText}
                </Button>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: SignedInPageActions,
    definition: {
        displayName: "Signed In Page Actions",
        group: "Sign In",
        allowedContexts: ["SignInPage"],
        fieldDefinitions: [
            {
                name: fields.firstButtonText,
                displayName: "First Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "What Can the Portal Do for You?",
            },
            {
                name: fields.firstButtonLink,
                displayName: "First Button Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "Instruction Videos",
                    type: "Page",
                },
                fieldType: "General",
            },
            {
                name: fields.firstButtonOpenInNewWindow,
                displayName: "Open First Button in New Window?",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.secondButtonText,
                displayName: "Second Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Is the Portal Available for You?",
            },
            {
                name: fields.secondButtonLink,
                displayName: "Second Button Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "Store Locator",
                    type: "Page",
                },
                fieldType: "General",
            },
            {
                name: fields.secondButtonOpenInNewWindow,
                displayName: "Open Second Button in New Window?",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.thirdButtonText,
                displayName: "Third Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Is the Portal Available for You?",
            },
            {
                name: fields.thirdButtonLink,
                displayName: "Third Button Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "Contact Us",
                    type: "Page",
                },
                fieldType: "General",
            },
            {
                name: fields.thirdButtonOpenInNewWindow,
                displayName: "Open Third Button in New Window?",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
